import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { Card, Row, Col, Input, Select, Button, Checkbox, Table, Space, Typography, Empty, DatePicker } from "antd";
import { UpOutlined, DownOutlined, FilePdfOutlined, FileTextOutlined, PrinterOutlined, BarsOutlined, UnorderedListOutlined } from "@ant-design/icons";

import Layout from "../../layout/Layout";

import fetchData from "../../functions/fetchData";

export default function SuppliersLedger() {
  const { RangePicker } = DatePicker;
  const { Title, Text } = Typography;
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [formData, setFormData] = useState({
    from: "",
    to: "",
    accountid: "",
    supplier_id: "",
    transaction_id: "",
    account_type: "",
    description: "",
    min_debit: "",
    max_debit: "",
    min_credit: "",
    max_credit: "",
  });
  const [totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showAllClosingBalance, setShowAllClosingBalance] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isLedgerTableCollapsed, setIsLedgerTableCollapsed] = useState(false);
  const [isClosingBalanceCollapsed, setIsClosingBalanceCollapsed] = useState(false);
  const [isGroupedView, setIsGroupedView] = useState(false);

  const accountTypeOptions = [
    { value: "supplier", label: "Supplier" },
    { value: "supplier_payment", label: "Supplier Payment" },
    { value: "purchase", label: "Purchase" },
  ];

  const getSupplierName = (accountId) => {
    const supplier = suppliers.find((s) => s.id?.toString() === accountId?.toString());
    return supplier ? supplier.company_name : `ID: ${accountId}`;
  };

  const columns = [
    {
      title: "Txn ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 100,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (value) => formatDate(value),
    },
    {
      title: "A/C Type",
      dataIndex: "account_type",
      key: "account_type",
    },
    {
      title: "Supplier Name",
      dataIndex: "account_id",
      key: "account_id",
      render: (account_id) => getSupplierName(account_id),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value) => value || "-",
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit_amount",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit_amount",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
  ];

  const formatDate = (d) => new Date(d).toISOString().split("T")[0];

  const normalizeAmount = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    const sanitized = String(value ?? "")
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");
    const parsed = parseFloat(sanitized);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const calculateTotals = (records) => {
    const credit = records.reduce((sum, r) => sum + parseFloat(r.credit_amount || 0), 0);
    const debit = records.reduce((sum, r) => sum + parseFloat(r.debit_amount || 0), 0);
    setData(records);
    setTotals({ credit, debit, balance: debit - credit });
  };

  const formatAmount = (value) => normalizeAmount(value).toFixed(2);

  const isPurchaseAccountRecord = (record) => (record?.account_type || "").toLowerCase() === "purchase";

  const groupRecordsBySupplier = (records) => {
    const grouped = records.reduce((acc, row) => {
      const supplierId = row.account_id?.toString() || "unknown";

      if (!acc[supplierId]) {
        acc[supplierId] = {
          key: supplierId,
          supplier_id: supplierId,
          supplier_name: getSupplierName(supplierId),
          transaction_count: 0,
          last_transaction_date: row.date || "",
          debit_total: 0,
          credit_total: 0,
          balance: 0,
        };
      }

      acc[supplierId].transaction_count += 1;
      acc[supplierId].debit_total += parseFloat(row.debit_amount || 0);
      acc[supplierId].credit_total += parseFloat(row.credit_amount || 0);
      acc[supplierId].balance = acc[supplierId].debit_total - acc[supplierId].credit_total;

      if (row.date && (!acc[supplierId].last_transaction_date || row.date > acc[supplierId].last_transaction_date)) {
        acc[supplierId].last_transaction_date = row.date;
      }

      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
  };

  const buildSupplierGroupedSections = (records) => {
    const supplierMap = records.reduce((acc, row) => {
      const supplierId = row.account_id?.toString() || "unknown";
      const dateKey = row.date ? formatDate(row.date) : "-";

      if (!acc[supplierId]) {
        acc[supplierId] = {
          key: supplierId,
          supplier_id: supplierId,
          supplier_name: getSupplierName(supplierId),
          debit_total: 0,
          credit_total: 0,
          balance: 0,
          entry_count: 0,
          days: new Set(),
          dateMap: {},
        };
      }

      const supplier = acc[supplierId];
      supplier.debit_total += normalizeAmount(row.debit_amount);
      supplier.credit_total += normalizeAmount(row.credit_amount);
      supplier.balance = supplier.debit_total - supplier.credit_total;
      supplier.entry_count += 1;
      supplier.days.add(dateKey);

      if (!supplier.dateMap[dateKey]) {
        supplier.dateMap[dateKey] = {
          key: `${supplierId}-${dateKey}`,
          date: dateKey,
          entries: 0,
          description: [],
          debit_amount: 0,
          credit_amount: 0,
          balance: 0,
        };
      }

      const dayRow = supplier.dateMap[dateKey];
      dayRow.entries += 1;
      dayRow.debit_amount += normalizeAmount(row.debit_amount);
      dayRow.credit_amount += normalizeAmount(row.credit_amount);
      dayRow.balance = dayRow.debit_amount - dayRow.credit_amount;

      const description = String(row.description || "").trim();
      if (description) {
        dayRow.description.push(description);
      }

      return acc;
    }, {});

    return Object.values(supplierMap)
      .map((supplier) => ({
        key: supplier.key,
        supplier_id: supplier.supplier_id,
        supplier_name: supplier.supplier_name,
        debit_total: supplier.debit_total,
        credit_total: supplier.credit_total,
        balance: supplier.balance,
        entry_count: supplier.entry_count,
        days_count: supplier.days.size,
        rows: Object.values(supplier.dateMap)
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((row) => ({
            ...row,
            description: row.description.join(", "),
          })),
      }))
      .sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
  };

  const groupedColumns = [
    {
      title: "Supplier ID",
      dataIndex: "supplier_id",
      key: "supplier_id",
      width: 120,
    },
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      key: "supplier_name",
    },
    {
      title: "Transactions",
      dataIndex: "transaction_count",
      key: "transaction_count",
      align: "center",
      width: 120,
    },
    {
      title: "Last Date",
      dataIndex: "last_transaction_date",
      key: "last_transaction_date",
      width: 130,
      render: (value) => (value ? formatDate(value) : "-"),
    },
    {
      title: "Debit",
      dataIndex: "debit_total",
      key: "debit_total",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Credit",
      dataIndex: "credit_total",
      key: "credit_total",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (value) => <Text strong>{parseFloat(value || 0).toFixed(2)}</Text>,
    },
  ];

  const getDateFilteredRecords = () => {
    const { from, to } = formData;
    const f = from ? formatDate(from) : null;
    const t = to ? formatDate(to) : null;

    return allData.filter((r) => {
      const date = formatDate(r.date);
      return (!f || date >= f) && (!t || date <= t);
    });
  };

  const applyFilters = () => {
    const {
      accountid,
      transaction_id,
      account_type,
      description,
      min_debit,
      max_debit,
      min_credit,
      max_credit,
    } = formData;

    const minDebit = min_debit === "" ? null : parseFloat(min_debit);
    const maxDebit = max_debit === "" ? null : parseFloat(max_debit);
    const minCredit = min_credit === "" ? null : parseFloat(min_credit);
    const maxCredit = max_credit === "" ? null : parseFloat(max_credit);

    const filtered = getDateFilteredRecords().filter((r) => {
      // Exclude customer ledger details and sales cash
      const excludedTypes = ["customer ledger details", "sales cash"];
      if (excludedTypes.includes((r.account_type || "").toLowerCase())) {
        return false;
      }

      const debit = parseFloat(r.debit_amount || 0);
      const credit = parseFloat(r.credit_amount || 0);
      const accountMatch = !accountid || r.account_id?.toString() === accountid?.toString();
      const txnMatch = !transaction_id || r.transaction_id?.toString().includes(transaction_id.toString());
      const accountTypeMatch = !account_type || (r.account_type || "").toLowerCase() === account_type.toLowerCase();
      const descriptionMatch = !description || (r.description || "").toLowerCase().includes(description.toLowerCase());
      const minDebitMatch = minDebit === null || debit >= minDebit;
      const maxDebitMatch = maxDebit === null || debit <= maxDebit;
      const minCreditMatch = minCredit === null || credit >= minCredit;
      const maxCreditMatch = maxCredit === null || credit <= maxCredit;

      return (
        accountMatch &&
        txnMatch &&
        accountTypeMatch &&
        descriptionMatch &&
        minDebitMatch &&
        maxDebitMatch &&
        minCreditMatch &&
        maxCreditMatch
      );
    });

    calculateTotals(filtered);
  };

  const handleInputChange = (name, value) => {
    if (name === "supplier_id") {
      const selected = suppliers.find((s) => s.id.toString() === value);
      setFormData((prev) => ({
        ...prev,
        supplier_id: value,
        accountid: selected ? selected.id : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetAdvancedFilters = () => {
    setFormData((prev) => ({
      ...prev,
      transaction_id: "",
      account_type: "",
      description: "",
      min_debit: "",
      max_debit: "",
      min_credit: "",
      max_credit: "",
    }));
  };

  const allSupplierClosingBalances = () => {
    const dateFilteredRecords = getDateFilteredRecords();
    const grouped = dateFilteredRecords.reduce((acc, row) => {
      const supplierId = row.account_id?.toString();
      if (!supplierId) return acc;

      if (!acc[supplierId]) {
        const supplier = suppliers.find((s) => s.id?.toString() === supplierId);
        acc[supplierId] = {
          key: supplierId,
          supplier_id: supplierId,
          supplier_name: supplier?.company_name || `Supplier ${supplierId}`,
          debit_total: 0,
          credit_total: 0,
          closing_balance: 0,
        };
      }

      acc[supplierId].debit_total += parseFloat(row.debit_amount || 0);
      acc[supplierId].credit_total += parseFloat(row.credit_amount || 0);
      acc[supplierId].closing_balance = acc[supplierId].debit_total - acc[supplierId].credit_total;

      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
  };

  const closingBalanceColumns = [
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
    },
    {
      title: "Supplier ID",
      dataIndex: "supplier_id",
      key: "supplier_id",
      width: 120,
    },
    {
      title: "Total Debit",
      dataIndex: "debit_total",
      key: "debit_total",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Total Credit",
      dataIndex: "credit_total",
      key: "credit_total",
      align: "right",
      render: (value) => parseFloat(value || 0).toFixed(2),
    },
    {
      title: "Closing Balance",
      dataIndex: "closing_balance",
      key: "closing_balance",
      align: "right",
      render: (value) => <Text strong>{parseFloat(value || 0).toFixed(2)}</Text>,
    },
  ];

  const exportPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["Txn ID", "Date", "A/C Type", "Supplier Name", "Description", "Debit", "Credit"];
    const fromDate = formData.from ? formatDate(formData.from) : "All";
    const toDate = formData.to ? formatDate(formData.to) : "All";
    const selectedSupplier = suppliers.find((s) => s.id?.toString() === formData.supplier_id?.toString());
    const selectedSupplierName = selectedSupplier
      ? selectedSupplier.company_name
      : formData.accountid
      ? `ID: ${formData.accountid}`
      : "All Suppliers";
    const filteredRecordsCount = data.length;
    const tableRows = data.map((r) => {
      const supplier = suppliers.find((s) => s.id?.toString() === r.account_id?.toString());
      const supplierName = supplier ? supplier.company_name : `ID: ${r.account_id}`;
      return [
        r.transaction_id,
        formatDate(r.date),
        r.account_type,
        supplierName,
        r.description,
        normalizeAmount(r.debit_amount).toFixed(2),
        normalizeAmount(r.credit_amount).toFixed(2),
      ];
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const headerLeft = 14;
    const headerRight = pageWidth - 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(35, 35, 35);
    doc.text("Supplier Ledger Report", headerLeft, 16);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(companyInfo?.company_name || "", headerLeft, 22);

    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(0.4);
    doc.line(headerLeft, 26, headerRight, 26);

    doc.setFontSize(10);
    doc.setTextColor(75, 75, 75);
    doc.text(`Date Range: ${fromDate} to ${toDate}`, headerLeft, 33);
    doc.text(`Supplier: ${selectedSupplierName}`, headerLeft, 39);
    doc.text(`Records: ${filteredRecordsCount}`, headerLeft, 45);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      theme: "grid",
      headStyles: {
        fillColor: [248, 248, 248],
        textColor: [40, 40, 40],
        lineColor: [230, 230, 230],
        lineWidth: 0.3,
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [55, 55, 55],
        lineColor: [235, 235, 235],
        lineWidth: 0.2,
      },
      styles: {
        fontSize: 9,
        cellPadding: 2.4,
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
      },
      margin: { left: 14, right: 14 },
    });

    const summaryY = doc.autoTable.previous.finalY + 10;
    doc.setDrawColor(225, 225, 225);
    doc.line(headerLeft, summaryY - 4, headerRight, summaryY - 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(35, 35, 35);
    doc.text(`Net Balance: ${totals.balance.toFixed(2)}`, headerLeft, summaryY + 1);
    doc.save("SupplierLedger.pdf");
  };

  const exportLedgerExcel = () => {
    const exportRows = data.map((row) => ({
      "Txn ID": row.transaction_id || "",
      Date: row.date ? formatDate(row.date) : "",
      "A/C Type": row.account_type || "",
      "Supplier Name": getSupplierName(row.account_id),
      Description: row.description || "-",
      Debit: formatAmount(row.debit_amount),
      Credit: formatAmount(row.credit_amount),
    }));

    exportRows.push({
      "Txn ID": "",
      Date: "",
      "A/C Type": "",
      "Supplier Name": "",
      Description: "Total",
      Debit: totals.debit.toFixed(2),
      Credit: totals.credit.toFixed(2),
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Supplier Ledger");
    XLSX.writeFile(workbook, `supplier_ledger_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const exportGroupedSupplierPDF = () => {
    const groupedData = buildSupplierGroupedSections(groupedPurchaseRecords);
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const tableColumn = ["Date", "Entries", "Description", "Debit", "Credit", "Balance"];
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 12;
    const marginRight = pageWidth - 12;
    const totalDebit = groupedData.reduce((sum, supplier) => sum + normalizeAmount(supplier.debit_total), 0);
    const totalCredit = groupedData.reduce((sum, supplier) => sum + normalizeAmount(supplier.credit_total), 0);
    const totalBalance = groupedData.reduce((sum, supplier) => sum + normalizeAmount(supplier.balance), 0);

    const renderPageHeader = (isFirstPage = false) => {
      doc.setFillColor(243, 248, 255);
      doc.roundedRect(marginLeft, 10, pageWidth - 24, isFirstPage ? 24 : 14, 3, 3, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(isFirstPage ? 16 : 13);
      doc.setTextColor(31, 41, 55);
      doc.text("Supplier Group Wise Day Report", marginLeft + 4, isFirstPage ? 19 : 19);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(companyInfo?.company_name || "", marginLeft + 4, isFirstPage ? 25 : 24);

      if (isFirstPage) {
        doc.text(`Date Range: ${formData.from || "All"} to ${formData.to || "All"}`, marginLeft + 150, 19);
        doc.text(`Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, marginLeft + 150, 25);
      }
    };

    const renderPageFooter = () => {
      doc.setDrawColor(226, 232, 240);
      doc.line(marginLeft, pageHeight - 10, marginRight, pageHeight - 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${doc.getNumberOfPages()}`, marginRight - 16, pageHeight - 5);
    };

    renderPageHeader(true);

    doc.setFillColor(249, 250, 251);
    doc.roundedRect(marginLeft, 38, pageWidth - 24, 18, 3, 3, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("Suppliers", marginLeft + 4, 45);
    doc.text("Total Debit", marginLeft + 72, 45);
    doc.text("Total Credit", marginLeft + 145, 45);
    doc.text("Grand Balance", marginLeft + 220, 45);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(String(groupedData.length), marginLeft + 4, 52);
    doc.text(formatAmount(totalDebit), marginLeft + 72, 52);
    doc.text(formatAmount(totalCredit), marginLeft + 145, 52);
    doc.text(formatAmount(totalBalance), marginLeft + 220, 52);

    let currentY = 64;

    groupedData.forEach((supplier, index) => {
      if (currentY > pageHeight - 46) {
        renderPageFooter();
        doc.addPage();
        renderPageHeader(false);
        currentY = 30;
      }

      doc.setDrawColor(219, 234, 254);
      doc.setFillColor(248, 251, 255);
      doc.roundedRect(marginLeft, currentY, pageWidth - 24, 14, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(43, 64, 94);
      doc.text(supplier.supplier_name, marginLeft + 4, currentY + 6);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(108, 122, 137);
      doc.text(`Supplier ID: ${supplier.supplier_id} | Days: ${supplier.days_count} | Entries: ${supplier.entry_count}`, marginLeft + 95, currentY + 6);

      doc.setFillColor(252, 252, 253);
      doc.rect(marginLeft, currentY + 14, pageWidth - 24, 12, "F");
      doc.setTextColor(100, 116, 139);
      doc.text("Days", marginLeft + 4, currentY + 20.5);
      doc.text("Debit", marginLeft + 72, currentY + 20.5);
      doc.text("Credit", marginLeft + 145, currentY + 20.5);
      doc.text("Balance", marginLeft + 220, currentY + 20.5);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(String(supplier.days_count), marginLeft + 4, currentY + 25);
      doc.text(formatAmount(supplier.debit_total), marginLeft + 72, currentY + 25);
      doc.text(formatAmount(supplier.credit_total), marginLeft + 145, currentY + 25);
      doc.text(formatAmount(supplier.balance), marginLeft + 220, currentY + 25);

      const tableRows = supplier.rows.map((row) => [
        row.date,
        row.entries,
        row.description || "-",
        formatAmount(row.debit_amount),
        formatAmount(row.credit_amount),
        formatAmount(row.balance),
      ]);
      tableRows.push([
        "Total",
        supplier.entry_count,
        "",
        formatAmount(supplier.debit_total),
        formatAmount(supplier.credit_total),
        formatAmount(supplier.balance),
      ]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: currentY + 28,
        theme: "grid",
        headStyles: {
          fillColor: [234, 242, 255],
          textColor: [30, 41, 59],
          lineColor: [219, 234, 254],
          lineWidth: 0.2,
          fontStyle: "bold",
        },
        bodyStyles: {
          textColor: [51, 65, 85],
          lineColor: [226, 232, 240],
          lineWidth: 0.15,
        },
        styles: {
          fontSize: 8.5,
          cellPadding: 2.4,
        },
        alternateRowStyles: {
          fillColor: [250, 252, 255],
        },
        columnStyles: {
          0: { cellWidth: 24 },
          1: { halign: "center", cellWidth: 16 },
          2: { cellWidth: 150 },
          3: { halign: "right", cellWidth: 26 },
          4: { halign: "right", cellWidth: 26 },
          5: { halign: "right", cellWidth: 28 },
        },
        margin: { left: marginLeft, right: 12 },
        didParseCell: (tableData) => {
          if (tableData.row.index === tableRows.length - 1) {
            tableData.cell.styles.fontStyle = "bold";
            tableData.cell.styles.fillColor = [245, 248, 255];
          }
        },
      });

      currentY = doc.autoTable.previous.finalY + (index === groupedData.length - 1 ? 0 : 10);
    });

    renderPageFooter();

    doc.save("SupplierLedgerGroupSummary.pdf");
  };

  const exportGroupedSupplierExcel = () => {
    const groupedData = buildSupplierGroupedSections(groupedPurchaseRecords);
    const exportRows = groupedData.flatMap((supplier) => [
      {
        "Supplier Name": supplier.supplier_name,
        "Supplier ID": supplier.supplier_id,
        Days: supplier.days_count,
        Entries: supplier.entry_count,
        Date: "",
        Description: "",
        Debit: formatAmount(supplier.debit_total),
        Credit: formatAmount(supplier.credit_total),
        Balance: formatAmount(supplier.balance),
      },
      ...supplier.rows.map((row) => ({
        "Supplier Name": "",
        "Supplier ID": "",
        Days: "",
        Entries: row.entries,
        Date: row.date,
        Description: row.description || "-",
        Debit: formatAmount(row.debit_amount),
        Credit: formatAmount(row.credit_amount),
        Balance: formatAmount(row.balance),
      })),
      {
        "Supplier Name": "",
        "Supplier ID": "",
        Days: "",
        Entries: "",
        Date: "",
        Description: "",
        Debit: "",
        Credit: "",
        Balance: "",
      },
    ]);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Group Wise Detail");
    XLSX.writeFile(workbook, `supplier_group_wise_detail_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const exportClosingBalancePDF = () => {
    const closingData = allSupplierClosingBalances();
    const doc = new jsPDF();
    const tableColumn = ["Supplier", "Supplier ID", "Total Debit", "Total Credit", "Closing Balance"];
    const tableRows = closingData.map((r) => [
      r.supplier_name,
      r.supplier_id,
      parseFloat(r.debit_total || 0).toFixed(2),
      parseFloat(r.credit_total || 0).toFixed(2),
      parseFloat(r.closing_balance || 0).toFixed(2),
    ]);

    const totalDebit = closingData.reduce((sum, row) => sum + parseFloat(row.debit_total || 0), 0);
    const totalCredit = closingData.reduce((sum, row) => sum + parseFloat(row.credit_total || 0), 0);
    const totalClosing = closingData.reduce((sum, row) => sum + parseFloat(row.closing_balance || 0), 0);

    tableRows.push([
      "TOTAL",
      "",
      totalDebit.toFixed(2),
      totalCredit.toFixed(2),
      totalClosing.toFixed(2),
    ]);

    const pageWidth = doc.internal.pageSize.getWidth();
    const headerLeft = 14;
    const headerRight = pageWidth - 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(35, 35, 35);
    doc.text("Supplier Closing Balance", headerLeft, 16);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(companyInfo?.company_name || "", headerLeft, 22);

    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(0.4);
    doc.line(headerLeft, 26, headerRight, 26);

    doc.setFontSize(10);
    doc.setTextColor(75, 75, 75);
    doc.text(`Suppliers: ${closingData.length}`, headerLeft, 33);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 38,
      theme: "grid",
      headStyles: {
        fillColor: [248, 248, 248],
        textColor: [40, 40, 40],
        lineColor: [230, 230, 230],
        lineWidth: 0.3,
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: [55, 55, 55],
        lineColor: [235, 235, 235],
        lineWidth: 0.2,
      },
      styles: {
        fontSize: 9,
        cellPadding: 2.4,
      },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
      margin: { left: 14, right: 14 },
      didParseCell: (tableData) => {
        if (tableData.row.index === tableRows.length - 1) {
          tableData.cell.styles.fontStyle = "bold";
          tableData.cell.styles.fillColor = [252, 252, 252];
        }
      },
    });
    doc.save("SupplierClosingBalance.pdf");
  };

  const printThermalReport = () => {
    const printWindow = window.open('', '', 'width=300,height=600');
    const companyName = companyInfo?.company_name || 'Company Name';
    const companyAddress = companyInfo?.address || 'Address';
    const companyPhone = companyInfo?.phone || 'Phone';

    const tableHtmlRows = data.map((r) => {
      const supplier = suppliers.find((s) => s.id?.toString() === r.account_id?.toString());
      const supplierName = supplier ? supplier.company_name : `ID: ${r.account_id}`;
      return `
        <tr>
          <td>${r.transaction_id}</td>
          <td>${formatDate(r.date)}</td>
          <td>${r.account_type}</td>
          <td>${supplierName}</td>
          <td>${r.description}</td>
          <td style="text-align: right;">${normalizeAmount(r.debit_amount).toFixed(2)}</td>
          <td style="text-align: right;">${normalizeAmount(r.credit_amount).toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Supplier Ledger Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12pt;
            margin: 0;
            padding: 5px 2px;
            width: 80mm;
          }
          .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 2px dashed #000;
            padding-bottom: 8px;
          }
          .company-name {
            font-size: 14pt;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .company-info {
            font-size: 10pt;
            margin: 2px 0;
          }
          .report-title {
            text-align: center;
            font-weight: bold;
            font-size: 13pt;
            margin: 10px 0;
          }
          .date-range {
            text-align: center;
            font-size: 10pt;
            margin-bottom: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 11pt;
          }
          th, td {
            text-align: left;
            padding: 4px 2px;
            border-bottom: 1px solid #ddd;
          }
          th {
            font-weight: bold;
            border-bottom: 2px solid #000;
          }
          .total-row {
            font-weight: bold;
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
          }
          .summary {
            margin-top: 15px;
            border-top: 2px dashed #000;
            padding-top: 10px;
            font-size: 12pt;
          }
          .summary-item {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
          }
          .summary-item.total {
            font-weight: bold;
            font-size: 13pt;
            border-top: 2px solid #000;
            padding-top: 5px;
            margin-top: 5px;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            font-size: 10pt;
            border-top: 2px dashed #000;
            padding-top: 8px;
          }
          @media print {
            body { margin: 0; padding: 5px 2px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${companyName}</div>
          <div class="company-info">${companyAddress}</div>
          <div class="company-info">Phone: ${companyPhone}</div>
        </div>
        
        <div class="report-title">Supplier Ledger Report</div>
        ${formData.from || formData.to ? `
          <div class="date-range">
            ${formData.from ? `From: ${formatDate(formData.from)}` : ''}
            ${formData.to ? ` To: ${formatDate(formData.to)}` : ''}
          </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Debit</th>
              <th>Credit</th>
            </tr>
          </thead>
          <tbody>
    `;

    data.forEach((item) => {
      html += `
        <tr>
          <td>${formatDate(item.date)}</td>
          <td>${item.description || '-'}</td>
          <td>${parseFloat(item.debit_amount || 0).toFixed(2)}</td>
          <td>${parseFloat(item.credit_amount || 0).toFixed(2)}</td>
        </tr>
      `;
    });

    html += `
          </tbody>
        </table>
        
        <div class="summary">
          <div class="summary-item">
            <span>Total Debit:</span>
            <span>${totals.debit.toFixed(2)}</span>
          </div>
          <div class="summary-item">
            <span>Total Credit:</span>
            <span>${totals.credit.toFixed(2)}</span>
          </div>
          <div class="summary-item total">
            <span>Balance:</span>
            <span>${totals.balance.toFixed(2)}</span>
          </div>
        </div>
        
        <div class="footer">
          Printed on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}<br/>
          Thank you!
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    fetchData("ledger_entries", (res) => {
      // Filter to show only Purchase and Supplier related transactions by default
      const excludedTypes = ["sales", "qr code", "cash", "customer ledger details", "sales cash"];
      const filteredData = res.filter(
        (r) => !excludedTypes.includes((r.account_type || "").toLowerCase())
      );
      setAllData(filteredData);
      setData(filteredData);
      calculateTotals(filteredData);
    }, "id", {});

    fetchData("suppliers", setSuppliers, "id", {});
    fetchData("companyinfo", (res) => setCompanyInfo(res[0]), "id", {});
  }, []);

  useEffect(() => {
    applyFilters();
  }, [
    formData.from,
    formData.to,
    formData.accountid,
    formData.transaction_id,
    formData.account_type,
    formData.description,
    formData.min_debit,
    formData.max_debit,
    formData.min_credit,
    formData.max_credit,
  ]);

  const groupedPurchaseRecords = data.filter(isPurchaseAccountRecord);
  const closingBalanceData = allSupplierClosingBalances();
  const groupedSupplierData = groupRecordsBySupplier(groupedPurchaseRecords);
  const groupedSupplierSections = buildSupplierGroupedSections(groupedPurchaseRecords);

  const headerCardStyle = {
    marginBottom: 16,
    borderRadius: 18,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    background: "linear-gradient(180deg, #fcfdff 0%, #f8fbff 100%)",
  };

  const tableCardStyle = {
    marginTop: 16,
    borderRadius: 18,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  };

  const groupedPalette = [
    {
      header: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)",
      summary: "#f6faff",
      border: "#dbeafe",
      tableHead: "#edf4ff",
    },
    {
      header: "linear-gradient(180deg, #fbfcff 0%, #f6f3ff 100%)",
      summary: "#faf7ff",
      border: "#e7ddff",
      tableHead: "#f2eeff",
    },
  ];

  const groupedDayColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: "Entries",
      dataIndex: "entries",
      key: "entries",
      width: 90,
      align: "center",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value) => value || "-",
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit_amount",
      width: 120,
      align: "right",
      render: (value) => formatAmount(value),
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit_amount",
      width: 110,
      align: "right",
      render: (value) => formatAmount(value),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      align: "right",
      render: (value) => <Text strong>{formatAmount(value)}</Text>,
    },
  ];

  const inputLabelStyle = {
    display: "block",
    marginBottom: 6,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
  };

  return (
    <Layout>
      <ToastContainer />
      <Card bordered={false} style={headerCardStyle} bodyStyle={{ padding: 20 }}>
        <div style={{ marginBottom: 14 }}>
          <Title level={4} style={{ marginBottom: 4 }}>Supplier Expense Ledger</Title>
          <Text type="secondary">Clean supplier ledger with soft colors for easier day-wise review.</Text>
        </div>

        <Row gutter={[14, 14]} align="bottom">
          <Col xs={24} md={8} lg={8}>
            <span style={inputLabelStyle}>Date Range</span>
            <RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              value={
                formData.from || formData.to
                  ? [formData.from ? dayjs(formData.from) : null, formData.to ? dayjs(formData.to) : null]
                  : null
              }
              onChange={(_, dateStrings) => {
                setFormData((prev) => ({
                  ...prev,
                  from: dateStrings?.[0] || "",
                  to: dateStrings?.[1] || "",
                }));
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span style={inputLabelStyle}>Supplier ID</span>
            <Input
              value={formData.accountid || ""}
              onChange={(e) => handleInputChange("accountid", e.target.value)}
              placeholder="Enter Supplier ID"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <span style={inputLabelStyle}>Supplier</span>
            <Select
              value={formData.supplier_id || undefined}
              onChange={(value) => handleInputChange("supplier_id", value || "")}
              placeholder="Select Supplier"
              allowClear
              style={{ width: "100%" }}
              options={suppliers.map((s) => ({ value: s.id.toString(), label: s.company_name }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <span style={inputLabelStyle}>A/C Type</span>
            <Select
              value={formData.account_type || undefined}
              onChange={(value) => handleInputChange("account_type", value || "")}
              placeholder="Select A/C type"
              allowClear
              style={{ width: "100%" }}
              options={accountTypeOptions}
            />
          </Col>
        </Row>

        <Row style={{ marginTop: 14 }}>
          <Col span={24}>
            <Space size="large" wrap>
              <Checkbox
                checked={showAdvancedFilters}
                onChange={(e) => setShowAdvancedFilters(e.target.checked)}
              >
                Show Advanced Filters
              </Checkbox>
            <Checkbox
              checked={showAllClosingBalance}
              onChange={(e) => setShowAllClosingBalance(e.target.checked)}
            >
              Show Closing Balance of All Suppliers
            </Checkbox>
            </Space>
          </Col>
        </Row>

        {showAdvancedFilters && (
          <Card size="small" style={{ marginTop: 16, background: "#fafafa", borderRadius: 14 }}>
            <Title level={5} style={{ marginBottom: 12 }}>Advanced Filters</Title>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={8}>
                <span style={inputLabelStyle}>Transaction ID</span>
                <Input
                  value={formData.transaction_id}
                  onChange={(e) => handleInputChange("transaction_id", e.target.value)}
                  placeholder="Contains transaction ID"
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <span style={inputLabelStyle}>Description</span>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Contains text"
                />
              </Col>
              <Col xs={24} sm={12} md={8} />
              <Col xs={24} sm={12} md={6}>
                <span style={inputLabelStyle}>Min Debit</span>
                <Input
                  type="number"
                  value={formData.min_debit}
                  onChange={(e) => handleInputChange("min_debit", e.target.value)}
                  placeholder="0"
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <span style={inputLabelStyle}>Max Debit</span>
                <Input
                  type="number"
                  value={formData.max_debit}
                  onChange={(e) => handleInputChange("max_debit", e.target.value)}
                  placeholder="0"
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <span style={inputLabelStyle}>Min Credit</span>
                <Input
                  type="number"
                  value={formData.min_credit}
                  onChange={(e) => handleInputChange("min_credit", e.target.value)}
                  placeholder="0"
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <span style={inputLabelStyle}>Max Credit</span>
                <Input
                  type="number"
                  value={formData.max_credit}
                  onChange={(e) => handleInputChange("max_credit", e.target.value)}
                  placeholder="0"
                />
              </Col>
            </Row>
            <Space style={{ marginTop: 12 }}>
              <Button onClick={resetAdvancedFilters}>Reset Advanced Filters</Button>
            </Space>
          </Card>
        )}

        <Space style={{ marginTop: 16 }} wrap>
          <Button type="primary" icon={<PrinterOutlined />} onClick={printThermalReport}>Print Thermal</Button>
          <Button
            icon={isGroupedView ? <UnorderedListOutlined /> : <BarsOutlined />}
            onClick={() => setIsGroupedView((prev) => !prev)}
          >
            {isGroupedView ? "Show Detail Ledger" : "Group Wise by Supplier"}
          </Button>
          <Button icon={<FileTextOutlined />} onClick={isGroupedView ? exportGroupedSupplierExcel : exportLedgerExcel}>
            Export Excel
          </Button>
          <Button danger icon={<FilePdfOutlined />} onClick={isGroupedView ? exportGroupedSupplierPDF : exportPDF}>Export PDF</Button>
        </Space>
      </Card>

      <Card
        bordered={false}
        style={tableCardStyle}
        title={isGroupedView ? "Supplier Group Wise Day Report" : "Supplier Ledger Table"}
        extra={
          <Space>
            {isGroupedView && (
              <>
                <Button icon={<FileTextOutlined />} onClick={exportGroupedSupplierExcel}>
                  Export Excel
                </Button>
                <Button danger icon={<FilePdfOutlined />} onClick={exportGroupedSupplierPDF}>
                  Export PDF
                </Button>
              </>
            )}
            <Button
              type="text"
              onClick={() => setIsLedgerTableCollapsed((prev) => !prev)}
              icon={isLedgerTableCollapsed ? <DownOutlined /> : <UpOutlined />}
            >
              {isLedgerTableCollapsed ? "Expand" : "Collapse"}
            </Button>
          </Space>
        }
      >
        {!isLedgerTableCollapsed && (
          <>
            {(isGroupedView ? groupedSupplierSections.length === 0 : data.length === 0) ? (
              <Empty description="No records found" />
            ) : isGroupedView ? (
              <div>
                <Card
                  bordered={false}
                  style={{
                    marginBottom: 14,
                    borderRadius: 16,
                    overflow: "hidden",
                    border: "1px solid #dbeafe",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
                    background: "linear-gradient(90deg, #f8fbff 0%, #fffaf4 100%)",
                  }}
                  bodyStyle={{ padding: "14px 18px" }}
                >
                  <Row gutter={[16, 12]}>
                    <Col xs={12} md={6}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Suppliers</div>
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>{groupedSupplierSections.length}</div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Total Debit</div>
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>{formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.debit_total), 0))}</div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Total Credit</div>
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>{formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.credit_total), 0))}</div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Grand Balance</div>
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>{formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.balance), 0))}</div>
                    </Col>
                  </Row>
                </Card>

                {groupedSupplierSections.map((supplier, index) => {
                  const palette = groupedPalette[index % groupedPalette.length];

                  return (
                    <Card
                      key={supplier.key}
                      bordered={false}
                      style={{
                        marginBottom: 14,
                        borderRadius: 16,
                        overflow: "hidden",
                        border: `1px solid ${palette.border}`,
                        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
                      }}
                      bodyStyle={{ padding: 0 }}
                    >
                      <div style={{ padding: "12px 16px", background: palette.header }}>
                        <div style={{ color: "#1e3a5f", fontWeight: 700, fontSize: 14 }}>{supplier.supplier_name}</div>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                          Supplier ID: {supplier.supplier_id} | Days: {supplier.days_count} | Entries: {supplier.entry_count}
                        </div>
                      </div>

                      <Row gutter={[12, 12]} style={{ padding: "10px 16px", background: palette.summary, borderTop: `1px solid ${palette.border}` }}>
                        <Col xs={12} md={6}>
                          <div style={{ color: "#64748b", fontSize: 12 }}>Days</div>
                          <div style={{ color: "#1f2937", fontWeight: 600 }}>{supplier.days_count}</div>
                        </Col>
                        <Col xs={12} md={6}>
                          <div style={{ color: "#64748b", fontSize: 12 }}>Debit</div>
                          <div style={{ color: "#1f2937", fontWeight: 600 }}>{formatAmount(supplier.debit_total)}</div>
                        </Col>
                        <Col xs={12} md={6}>
                          <div style={{ color: "#64748b", fontSize: 12 }}>Credit</div>
                          <div style={{ color: "#1f2937", fontWeight: 600 }}>{formatAmount(supplier.credit_total)}</div>
                        </Col>
                        <Col xs={12} md={6}>
                          <div style={{ color: "#64748b", fontSize: 12 }}>Balance</div>
                          <div style={{ color: "#1f2937", fontWeight: 700 }}>{formatAmount(supplier.balance)}</div>
                        </Col>
                      </Row>

                      <Table
                        rowKey="key"
                        columns={groupedDayColumns}
                        dataSource={supplier.rows}
                        pagination={false}
                        size="small"
                        scroll={{ x: 900 }}
                        style={{ margin: 0 }}
                        summary={() => (
                          <Table.Summary.Row style={{ background: "#f8fbff" }}>
                            <Table.Summary.Cell index={0}>
                              <Text strong>Total</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">
                              <Text strong>{supplier.entry_count}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} />
                            <Table.Summary.Cell index={3} align="right">
                              <Text strong>{formatAmount(supplier.debit_total)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                              <Text strong>{formatAmount(supplier.credit_total)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="right">
                              <Text strong>{formatAmount(supplier.balance)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                      />
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Table
                rowKey={(record, index) => `${record.transaction_id || record.supplier_id || 'row'}-${index}`}
                columns={isGroupedView ? groupedColumns : columns}
                dataSource={isGroupedView ? groupedSupplierData : data}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 900 }}
                summary={(pageData) => {
                  if (isGroupedView) {
                    const pageDebit = pageData.reduce((sum, row) => sum + parseFloat(row.debit_total || 0), 0);
                    const pageCredit = pageData.reduce((sum, row) => sum + parseFloat(row.credit_total || 0), 0);
                    const pageBalance = pageData.reduce((sum, row) => sum + parseFloat(row.balance || 0), 0);

                    return (
                      <Table.Summary.Row style={{ background: "#f8fafc" }}>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Page Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong>{pageDebit.toFixed(2)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong>{pageCredit.toFixed(2)}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong>{pageBalance.toFixed(2)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }

                  const pageDebit = pageData.reduce((sum, row) => sum + parseFloat(row.debit_amount || 0), 0);
                  const pageCredit = pageData.reduce((sum, row) => sum + parseFloat(row.credit_amount || 0), 0);
                  const pageBalance = pageDebit - pageCredit;

                  return (
                    <Table.Summary.Row style={{ background: "#f8fafc" }}>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <Text strong>Page Total Amount</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <Text strong>{pageDebit.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <Text strong>{pageCredit.toFixed(2)}</Text>
                        <div style={{ color: "#64748b", fontSize: 12 }}>Balance: {pageBalance.toFixed(2)}</div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            )}

            <div style={{ marginTop: 12, paddingTop: 8 }}>
              {isGroupedView ? (
                <Text>
                  <Text strong>Suppliers:</Text> {groupedSupplierSections.length} | <Text strong>Days:</Text> {groupedSupplierSections.reduce((sum, row) => sum + Number(row.days_count || 0), 0)} | <Text strong>Total Debit:</Text> {formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.debit_total), 0))} | <Text strong>Total Credit:</Text> {formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.credit_total), 0))} | <Text strong>Balance:</Text> {formatAmount(groupedSupplierSections.reduce((sum, row) => sum + normalizeAmount(row.balance), 0))}
                </Text>
              ) : (
                <Text>
                  <Text strong>Total Credit:</Text> {totals.credit.toFixed(2)} | <Text strong>Total Debit:</Text> {totals.debit.toFixed(2)} | <Text strong>Balance:</Text> {totals.balance.toFixed(2)}
                </Text>
              )}
            </div>
          </>
        )}
      </Card>

      {showAllClosingBalance && (
        <Card
          bordered={false}
          style={tableCardStyle}
          title="Closing Balance of All Suppliers"
          extra={
            <Space>
              <CSVLink
                data={[
                  ...closingBalanceData,
                  {
                    supplier_name: "TOTAL",
                    supplier_id: "",
                    debit_total: closingBalanceData.reduce((sum, row) => sum + parseFloat(row.debit_total || 0), 0).toFixed(2),
                    credit_total: closingBalanceData.reduce((sum, row) => sum + parseFloat(row.credit_total || 0), 0).toFixed(2),
                    closing_balance: closingBalanceData.reduce((sum, row) => sum + parseFloat(row.closing_balance || 0), 0).toFixed(2),
                  },
                ]}
                filename="SupplierClosingBalance.csv"
              >
                <Button>Export Excel</Button>
              </CSVLink>
              <Button danger onClick={exportClosingBalancePDF}>Export PDF</Button>
              <Button
                type="text"
                onClick={() => setIsClosingBalanceCollapsed((prev) => !prev)}
                icon={isClosingBalanceCollapsed ? <DownOutlined /> : <UpOutlined />}
              >
                {isClosingBalanceCollapsed ? "Expand" : "Collapse"}
              </Button>
            </Space>
          }
        >
          {!isClosingBalanceCollapsed && (
            <Table
              rowKey="key"
              columns={closingBalanceColumns}
              dataSource={closingBalanceData}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: "No supplier balance data found" }}
            />
          )}
        </Card>
      )}
    </Layout>
  );
}